import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQueryState, parseAsStringLiteral } from "nuqs";
import { Plan, Employee } from "@shared/schema";
import { AddPlanDialog } from "@/components/ui/plans/AddPlanDialog";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import BenefitsOverview from "./BenefitsTab/BenefitsOverview";
import AvailablePlans from "./BenefitsTab/AvailablePlans";
import OpenEnrollment from "./BenefitsTab/OpenEnrollment";
import EnrollmentsTab from "./BenefitsTab/EnrollmentsTab";
import EmployeeSummary from "./BenefitsTab/EmployeeSummary";
import { Button } from "@/components/ui/button";
import { BenefitsDisplaySettingsCard } from "@/components/benefits/BenefitsDisplaySettingsCard";
import PremiumLedgerCard from "@/components/premiums/PremiumLedgerCard";

const BENEFITS_SUBTABS = [
  "overview",
  "enrollments",
  "open-enrollment",
  "employee-summary",
  "ledger",
] as const;

interface BenefitsTabProps {
  plansData: Plan[];
  /** Every plan year of every plan (incl. an upcoming renewal year) for the plan list. */
  planYearsData?: Plan[];
  employees: Employee[];
  clientId?: string;
  prospectId?: string;
  plansLoading: boolean;
  getEffectivePlan: (plan: Plan) => Plan;
  formatDateSafe: (date: string | Date | null | undefined) => string;
  getPlanRate: (planId: number, coverageTier: string) => number;
  calculateContributions: (
    planId: number,
    coverageTier: string,
    totalPremium: number
  ) => { employerContribution: number; employeeContribution: number };
  deletePlanMutation: any;
  setLocation: (location: string) => void;
}

const BenefitsTab = ({
  plansData,
  planYearsData,
  employees,
  clientId,
  prospectId,
  plansLoading,
  getEffectivePlan,
  formatDateSafe,
  getPlanRate,
  calculateContributions,
  deletePlanMutation,
  setLocation,
}: BenefitsTabProps) => {
  const [isAddPlanDialogOpen, setIsAddPlanDialogOpen] = useState(false);

  // URL-based subtab persistence using nuqs
  const [activeSubTab, setActiveSubTab] = useQueryState(
    "subtab",
    parseAsStringLiteral(BENEFITS_SUBTABS).withDefault("overview")
  );

  // Filter employees by clientId or prospectId depending on context
  const entityId = clientId || prospectId;
  const clientEmployees = employees.filter((emp) => {
    if (clientId) {
      return emp.clientId === parseInt(clientId);
    }
    if (prospectId) {
      return (emp as any).prospectId === parseInt(prospectId);
    }
    return false;
  });

  const openEnrollmentsTab = () => {
    setActiveSubTab("enrollments");
  };

  const openAddPlanDialog = () => {
    setIsAddPlanDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Benefits & Plans</h3>
      </div>

      {/* Benefits Sub-navigation with compact button group */}
      <Tabs
        value={activeSubTab}
        onValueChange={(value) => setActiveSubTab(value as typeof BENEFITS_SUBTABS[number])}
        className="space-y-4"
      >
        <div className="flex items-center gap-2 border-b pb-2">
          <span className="text-sm font-medium text-muted-foreground">
            View:
          </span>
          <TabsList className="h-9 bg-muted">
            <TabsTrigger value="overview" className="text-xs h-7">
              Overview
            </TabsTrigger>
            <TabsTrigger value="enrollments" className="text-xs h-7">
              Enrollments
            </TabsTrigger>
            <TabsTrigger value="open-enrollment" className="text-xs h-7">
              Open Enrollment
            </TabsTrigger>
            <TabsTrigger value="employee-summary" className="text-xs h-7">
              Employee Summary
            </TabsTrigger>
            {clientId && (
              <TabsTrigger value="ledger" className="text-xs h-7">
                Premium Ledger
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-4">
          <BenefitsOverview
            plansData={plansData}
            employees={employees}
            clientId={clientId}
            prospectId={prospectId}
            getEffectivePlan={getEffectivePlan}
            formatDateSafe={formatDateSafe}
          />

          <AvailablePlans
            plansData={planYearsData ?? plansData}
            plansLoading={plansLoading}
            getEffectivePlan={getEffectivePlan}
            formatDateSafe={formatDateSafe}
            deletePlanMutation={deletePlanMutation}
            // Library model: plans are shared — "delete" from a client context
            // UNLINKS the client_plans association, never the plan row.
            deleteMode="removeFromClient"
            setLocation={setLocation}
            clientId={clientId}
            prospectId={prospectId}
            onAddPlanClick={openAddPlanDialog}
          />

          {clientId && <BenefitsDisplaySettingsCard clientId={parseInt(clientId)} />}
        </TabsContent>

        <TabsContent value="enrollments" className="space-y-4">
          <EnrollmentsTab
            plansData={plansData}
            clientEmployees={clientEmployees}
            clientId={clientId}
            prospectId={prospectId}
          />
        </TabsContent>

        <TabsContent value="open-enrollment" className="space-y-4">
          <OpenEnrollment
            clientEmployees={clientEmployees}
            clientId={clientId}
            prospectId={prospectId}
            plansData={plansData}
            planYearsData={planYearsData}
            plansLoading={plansLoading}
          />
        </TabsContent>

        <TabsContent value="employee-summary" className="space-y-4">
          <EmployeeSummary
            employees={clientEmployees as any}
            plansData={plansData as any}
            clientId={clientId}
            prospectId={prospectId}
          />
        </TabsContent>

        {clientId && (
          <TabsContent value="ledger" className="space-y-4">
            <PremiumLedgerCard clientId={parseInt(clientId)} />
          </TabsContent>
        )}
      </Tabs>

      <ErrorBoundary>
        <AddPlanDialog
          open={isAddPlanDialogOpen}
          onOpenChange={setIsAddPlanDialogOpen}
          clientId={clientId}
          prospectId={prospectId}
        />
      </ErrorBoundary>
    </div>
  );
};

export default BenefitsTab;
